import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactFlow, { Background, Controls, type Node, type Edge } from 'reactflow'
import 'reactflow/dist/style.css'
import { ResponsiveSankey } from '@nivo/sankey'
import { useOrgStore } from '@/stores/orgStore'
import { graphApi } from '@/api/clients/graphApi'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { PageHeader, Card } from '@/components/common/ui'
import { Spinner, ErrorState, EmptyState } from '@/components/common/State'
import { MoneyValue } from '@/components/common/MoneyValue'
import { familyOf } from '@/lib/cryptoFamily'

type View = 'graph' | 'sankey'

const BAND_COLOR: Record<string, string> = {
  critical: '#C22221',
  high: '#e8734f',
  medium: '#eab308',
  low: '#94C8E4',
  info: '#94a3b8',
}

const NODE_CAP = 300
const TOP_CLUSTERS = 60

// Band severity order (worst → best) for picking a cluster's color.
const BAND_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 }
function worseBand(a: string, b: string): string {
  return (BAND_ORDER[a] ?? 4) <= (BAND_ORDER[b] ?? 4) ? a : b
}

export default function Graph() {
  const { currentOrgId: orgId } = useOrgStore()
  const [view, setView] = useState<View>('graph')

  const views: { key: View; label: string; icon: string }[] = [
    { key: 'graph', label: 'Blast radius', icon: 'hub' },
    { key: 'sankey', label: 'Sankey (data flow)', icon: 'account_tree' },
  ]

  return (
    <div>
      <PageHeader
        title="Risk Graph"
        subtitle="Blast-radius exploration — drill-in lens, not a landing page (design §5.5)"
      />
      <div className="flex gap-1 border-b border-border-light dark:border-border-dark mb-5">
        {views.map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === v.key
                ? 'border-primary text-primary dark:border-secondary dark:text-secondary'
                : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            <span className="material-icons text-base">{v.icon}</span>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'graph' && <GraphView orgId={orgId} />}
      {view === 'sankey' && <SankeyView orgId={orgId} />}
    </div>
  )
}

export function GraphView({ orgId }: { orgId: string }) {
  const graph = useQuery({
    queryKey: ['graph', orgId],
    queryFn: () => graphApi.graph(orgId),
    enabled: !!orgId,
    retry: false,
  })
  const summary = useQuery({
    queryKey: ['summary', orgId],
    queryFn: () => analyticsApi.summary(orgId),
    enabled: !!orgId,
    retry: false,
  })

  const { nodes, edges, capped, clusterMode, totalClusters, totalFamilies } = useMemo(() => {
    const empty = { nodes: [] as Node[], edges: [] as Edge[], capped: false, clusterMode: false, totalClusters: 0, totalFamilies: 0 }
    const g = graph.data
    if (!g?.nodes) return empty
    const rawEdges = g.edges ?? []
    const nodeById = new Map(g.nodes.map((n) => [n.id, n]))
    const sameEntity = rawEdges.filter((e) => e.type === 'same-entity')

    // ── Cluster-collapsed view: one node per shared-crypto-entity (key/cert/algorithm)
    // reused across the estate, sized by how many assets share it. Avoids the
    // 1000s-of-nodes-and-spokes hairball; the reuse count is the blast radius.
    if (sameEntity.length > 0) {
      // Group members by hub (the same-entity edge target = cluster representative).
      const members = new Map<string, Set<string>>()
      sameEntity.forEach((e) => {
        const set = members.get(e.target) ?? new Set<string>([e.target])
        set.add(e.source)
        members.set(e.target, set)
      })
      const todayISO = new Date().toISOString().slice(0, 10)
      const clusters = [...members.entries()]
        .map(([hubId, set]) => {
          let band = nodeById.get(hubId)?.band ?? 'info'
          let hndlNow = false
          let overdue = false
          set.forEach((id) => {
            const m = nodeById.get(id)
            band = worseBand(band as string, (m?.band as string) ?? 'info')
            if (typeof m?.hndl_window === 'number' && m.hndl_window <= 0) hndlNow = true
            if (m?.remediation_deadline && m.remediation_deadline < todayISO) overdue = true
          })
          const fam = familyOf(String(nodeById.get(hubId)?.algorithm ?? ''))
          return { hubId, count: set.size, band: band as string, label: nodeById.get(hubId)?.label ?? 'entity', fam, hndlNow, overdue }
        })
        .sort((a, b) => b.count - a.count)
      const capped = clusters.length > TOP_CLUSTERS
      const top = clusters.slice(0, TOP_CLUSTERS)

      // Group clusters by algorithm family → each family is a hub the reuse bubbles
      // attach to (short intra-sector edges instead of a global spoke hairball).
      const famList = [...new Map(top.map((c) => [c.fam.label, c.fam])).values()]
      const byFam = new Map<string, typeof top>()
      top.forEach((c) => {
        const arr = byFam.get(c.fam.label) ?? []
        arr.push(c)
        byFam.set(c.fam.label, arr)
      })
      const F = Math.max(1, famList.length)
      const Ri = Math.max(200, F * 60) // family hubs on an inner ring
      // Each family is a radial SPOKE: hub on the inner ring, then its reuse bubbles
      // stacked straight outward along the family's angle, spaced by bubble size — so
      // nothing overlaps. fitView frames the whole thing (coords may be negative).
      const nodes: Node[] = []
      const edges: Edge[] = []
      famList.forEach((f, fi) => {
        const famAngle = (fi / F) * 2 * Math.PI
        const ux = Math.cos(famAngle)
        const uy = Math.sin(famAngle)
        nodes.push({
          id: `fam:${f.label}`,
          position: { x: Ri * ux, y: Ri * uy },
          data: { label: f.label },
          style: {
            background: f.color,
            color: '#fff',
            border: '2px solid #fff',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            width: 128,
            textAlign: 'center' as const,
            padding: 6,
          },
        })
        const group = byFam.get(f.label) ?? []
        let r = Ri + 120 // start just outside the hub
        group.forEach((c) => {
          const w = Math.min(180, Math.round(56 + Math.sqrt(c.count) * 20))
          r += w / 2
          // Business-value ring: red = harvest-now exposed, amber = past PQC deadline.
          const ring = c.hndlNow ? '3px solid #C22221' : c.overdue ? '3px solid #f59e0b' : 'none'
          nodes.push({
            id: c.hubId,
            position: { x: r * ux - w / 2, y: r * uy - w / 2 },
            data: { label: `${c.label} ×${c.count}` },
            style: {
              background: BAND_COLOR[c.band] ?? '#94a3b8',
              color: '#fff',
              border: ring,
              borderRadius: 9999,
              fontSize: 11,
              width: w,
              height: w,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center' as const,
              padding: 4,
            },
          })
          edges.push({
            id: `f${c.hubId}`,
            source: c.hubId,
            target: `fam:${f.label}`,
            animated: false,
            style: { stroke: '#94a3b8' },
          })
          r += w / 2 + 28 // advance outward past this bubble (+ gap)
        })
      })
      return { nodes, edges, capped, clusterMode: true, totalClusters: clusters.length, totalFamilies: famList.length }
    }

    // ── Fallback: dependency-edge graph (datasets that carry relationships). Keep
    // connected + highest-risk nodes within the cap so edges actually render.
    const connected = new Set<string>()
    rawEdges.forEach((e) => {
      connected.add(e.source)
      connected.add(e.target)
    })
    const sorted = [...g.nodes].sort((a, b) => {
      const ca = connected.has(a.id) ? 0 : 1
      const cb = connected.has(b.id) ? 0 : 1
      if (ca !== cb) return ca - cb
      return (Number(b.score) || 0) - (Number(a.score) || 0)
    })
    const capped = g.nodes.length > NODE_CAP
    const src = sorted.slice(0, NODE_CAP)
    const R = Math.max(220, src.length * 12)
    const nodes: Node[] = src.map((n, i) => {
      const angle = (i / src.length) * 2 * Math.PI
      return {
        id: n.id,
        position: { x: R + R * Math.cos(angle), y: R + R * Math.sin(angle) },
        data: { label: n.label },
        style: {
          background: BAND_COLOR[n.band as string] ?? '#94a3b8',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 11,
          width: 120,
        },
      }
    })
    const present = new Set(src.map((n) => n.id))
    const edges: Edge[] = rawEdges
      .filter((e) => present.has(e.source) && present.has(e.target))
      .map((e, i) => ({ id: `e${i}`, source: e.source, target: e.target, animated: false, style: { stroke: '#64748b' } }))
    return { nodes, edges, capped, clusterMode: false, totalClusters: 0, totalFamilies: 0 }
  }, [graph.data])

  if (graph.isLoading) return <Spinner />
  if (graph.error)
    return (
      <EmptyState
        icon="hub"
        title="Risk graph unavailable"
        hint="Graph data ships in P3+ (relationships). Present only once graph data is populated."
      />
    )
  if (nodes.length === 0)
    return <EmptyState icon="hub" title="No graph nodes" hint="No asset relationships to visualise yet." />

  const money = summary.data?.money
  return (
    <>
      {money && (
        <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-xl border border-border-light dark:border-border-dark px-4 py-2.5">
          <span className="text-sm">
            <span className="text-subtle-light dark:text-subtle-dark">Estate $-at-Risk </span>
            <span className="font-semibold text-text-light dark:text-text-dark">
              <MoneyValue value={money.total} currency={money.currency} />
            </span>
            {money.confidence && (
              <span className="ml-1 text-xs text-subtle-light dark:text-subtle-dark">· {money.confidence} confidence</span>
            )}
          </span>
          {(money.top ?? []).slice(0, 3).map((t) => (
            <span key={t.name} className="text-xs text-subtle-light dark:text-subtle-dark">
              {t.name}: <MoneyValue value={t.value} currency={money.currency} />
            </span>
          ))}
        </div>
      )}
      <Card className="!p-0">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-xs border-b border-border-light dark:border-border-dark">
          {clusterMode ? (
            <>
              <span className="text-subtle-light dark:text-subtle-dark">
                {totalClusters.toLocaleString()} crypto-reuse {totalClusters === 1 ? 'cluster' : 'clusters'} across{' '}
                {totalFamilies.toLocaleString()} algorithm {totalFamilies === 1 ? 'family' : 'families'} · bubble size =
                assets sharing one key/cert
              </span>
              <span className="flex items-center gap-1.5 text-subtle-light dark:text-subtle-dark">
                <span className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: '#C22221' }} /> harvest-now exposed
              </span>
              <span className="flex items-center gap-1.5 text-subtle-light dark:text-subtle-dark">
                <span className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: '#f59e0b' }} /> past PQC deadline
              </span>
              {capped && <span className="text-warning">Showing top {TOP_CLUSTERS} by blast radius.</span>}
            </>
          ) : (
            capped && (
              <span className="text-warning">Showing top {NODE_CAP} nodes (connected + highest-risk).</span>
            )
          )}
        </div>
      <div className="h-[600px]">
        <ReactFlow nodes={nodes} edges={edges} fitView minZoom={0.1} proOptions={{ hideAttribution: true }}>
          <Background />
          <Controls />
        </ReactFlow>
      </div>
      </Card>
    </>
  )
}

function SankeyView({ orgId }: { orgId: string }) {
  const sankey = useQuery({
    queryKey: ['sankey', orgId],
    queryFn: () => graphApi.sankey(orgId),
    enabled: !!orgId,
    retry: false,
  })
  if (sankey.isLoading) return <Spinner />
  if (sankey.error) return <ErrorState error={sankey.error} />
  if (!sankey.data?.nodes?.length)
    return <EmptyState icon="account_tree" title="No flow data" />
  return (
    <Card>
      <div className="h-[600px]">
        <ResponsiveSankey
          data={sankey.data}
          margin={{ top: 20, right: 120, bottom: 20, left: 120 }}
          align="justify"
          colors={{ scheme: 'blues' }}
          nodeOpacity={1}
          nodeThickness={16}
          linkOpacity={0.4}
          labelPosition="outside"
        />
      </div>
    </Card>
  )
}
